import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { withTranslation } from "react-i18next";
import { Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button } from "reactstrap";
import { Link, useHistory, useParams } from "react-router-dom";
import Breadcrumbs from "components/Common/Breadcrumb";
import { CategoryApi } from "apis/CategoryApi";
import Swal from "../../helpers/swal_helper";
function EditCategory(props) {
    const history = useHistory();
    const { id } = useParams();
    const [name, setName] = useState("");
    const [status, setStatus] = useState(1);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [existingImage, setExistingImage] = useState(null);

    useEffect(() => {
        fetchCategoryDetails();
    }, [id]);

    const fetchCategoryDetails = () => {
        setFetchLoading(true);
        CategoryApi.getCategoryById(id)
            .then((res) => {
                if (res.data.status) {
                    const categoryData = res.data.data;
                    setName(categoryData.name);
                    setStatus(categoryData.status);
                    if (categoryData.image) {
                        setExistingImage(categoryData.image);
                        setImagePreview(categoryData.image);
                    }
                } else {
                    Swal.fire({
                        title: "Error",
                        text: res.data.message,
                        icon: "error",
                    }).then(() => {
                        history.push("/categories");
                    });
                }
                setFetchLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setFetchLoading(false);
                Swal.fire({
                    title: "Error",
                    text: "Failed to fetch category details",
                    icon: "error",
                }).then(() => {
                    history.push("/categories");
                });
            });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        let tempErrors = {};
        if (!name.trim()) tempErrors.name = props.t("Name is required");
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            setLoading(true);
            const formData = new FormData();
            formData.append('id', id);
            formData.append('name', name);
            formData.append('status', parseInt(status));
            if (image) {
                formData.append('image', image);
            }

            CategoryApi.updateCategory(formData)
                .then((res) => {
                    setLoading(false);
                    if (res.data.status) {
                        Swal.fire({
                            title: "Success",
                            text: res.data.message,
                            icon: "success",
                        }).then(() => {
                            history.push("/categories");
                        });
                    } else {
                        Swal.fire({
                            title: "Error",
                            text: res.data.message,
                            icon: "error",
                        });
                    }
                })
                .catch((err) => {
                    setLoading(false);
                    console.log(err);
                    Swal.fire({
                        title: "Error",
                        text: err.response.data.message || "Failed to update category",
                        icon: "error",
                    });
                });
        }
    };

    //meta title
    document.title = props.t("Edit Category") + " | " + props.t("Salon");

    if (fetchLoading) {
        return (
            <div className="page-content">
                <div className="container-fluid">
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">{props.t("Loading...")}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumbs title={props.t("Categories")} breadcrumbItem={props.t("Edit Category")} />

                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label htmlFor="name">{props.t("Category Name")}</Label>
                                                <Input
                                                    type="text"
                                                    id="name"
                                                    placeholder={props.t("Enter category name")}
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    invalid={!!errors.name}
                                                />
                                                {errors.name && (
                                                    <div className="invalid-feedback">{errors.name}</div>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label htmlFor="status">{props.t("Status")}</Label>
                                                <Input
                                                    type="select"
                                                    value={status}
                                                    onChange={(e) => setStatus(e.target.value)}
                                                    style={{ height: "38px", width: "100%" }}
                                                >
                                                    <option value={1}>{props.t("Active")}</option>
                                                    <option value={0}>{props.t("Inactive")}</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label htmlFor="image">{props.t("Category Image")}</Label>
                                                <Input
                                                    type="file"
                                                    id="image"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                    invalid={!!errors.image}
                                                />
                                                {errors.image && (
                                                    <div className="invalid-feedback">{errors.image}</div>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            {imagePreview && (
                                                <div className="mt-2">
                                                    <img
                                                        src={imagePreview}
                                                        alt="Preview"
                                                        style={{ maxWidth: "200px", maxHeight: "200px" }}
                                                        className="img-thumbnail"
                                                    />
                                                </div>
                                            )}
                                        </Col>
                                    </Row>

                                    <div className="d-flex flex-wrap gap-2">
                                        <Button type="submit" color="primary" disabled={loading}>
                                            {loading ? props.t("Updating...") : props.t("Update")}
                                        </Button>
                                        <Link to="/categories" className="btn btn-secondary">
                                            {props.t("Cancel")}
                                        </Link>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
}

EditCategory.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(EditCategory); 